import { AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'

type BaseProps = {
  variant?: 'gray' | 'black'
  small?: boolean
  children: React.ReactNode
}

type ButtonSecondaryButtonProps = BaseProps &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: never
  }

type AnchorSecondaryButtonProps = BaseProps & {
  href: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>

export function SecondaryButton(props: ButtonSecondaryButtonProps): JSX.Element
export function SecondaryButton(props: AnchorSecondaryButtonProps): JSX.Element
export function SecondaryButton(
  props: ButtonSecondaryButtonProps | AnchorSecondaryButtonProps
) {
  const { children, small, variant, className, ...restProps } = props
  const baseClassName = `text-base text-mydra-black outline outline-1 text-center leading-6
    ${small ? 'px-3 py-2 text-sm rounded-lg' : 'px-4 py-3 text-base rounded-xl'}
    ${variant === 'black' ? 'outline-black' : 'outline-[--lines]'}
    ${className ?? ''}`

  // Check if it's a link by checking for href property
  if ('href' in props && props.href) {
    const isExternal =
      props.href.startsWith('http') || props.href.startsWith('mailto:')

    const linkClassName = `${baseClassName} hover:!bg-mydra-gray`

    if (isExternal) {
      return (
        <a href={props.href} onClick={props.onClick} className={linkClassName}>
          {children}
        </a>
      )
    } else {
      return (
        <Link to={props.href} onClick={props.onClick} className={linkClassName}>
          {children}
        </Link>
      )
    }
  }

  // It's a button - we know restProps is ButtonHTMLAttributes here due to overloads
  const disabled = 'disabled' in restProps ? restProps.disabled : false

  const buttonClassName = `${baseClassName} ${
    disabled
      ? 'opacity-50 cursor-not-allowed outline-lines'
      : 'hover:!bg-mydra-gray'
  }`

  return (
    // @ts-expect-error - union type props spreading issue
    <button {...restProps} className={buttonClassName}>
      {children}
    </button>
  )
}
